import type React from 'react'
import { useState, useRef, useEffect, useCallback } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { useStream } from '@langchain/langgraph-sdk/react'
import type { Message } from '@langchain/langgraph-sdk'
import { ChatBubble, ChatTypingIndicator, ChatWidget, Button } from '@knownow/ui'
import type { WidgetConfig, WidgetResponse } from '@knownow/ui'
import { isRtl, languageDirection } from '@knownow/domain'
import { MarkdownContent } from '@/components/markdown-content'
import { getMessageText, buildChatItems, formatWidgetResponse } from '@/lib/message-utils'
import { supabase } from '@/lib/supabase'

const AGENT_URL =
  (import.meta.env['VITE_AGENT_URL'] as string | undefined) ?? 'http://127.0.0.1:3001'

const COMPLETION_MARKER = '__CONVERSATION_COMPLETE__'

interface WidgetInterrupt {
  kind: 'widget'
  widget: WidgetConfig
  informationNeedId: string
}

function isCompleted(messages: Message[]): boolean {
  return messages.some((m) => m.type === 'ai' && getMessageText(m).includes(COMPLETION_MARKER))
}

function getWidgetInterrupt(interrupt: unknown): WidgetInterrupt | null {
  if (interrupt == null) return null

  // useStream returns Interrupt<T> with { value } — extract the value
  const raw = typeof interrupt === 'object' && 'value' in (interrupt as Record<string, unknown>)
    ? (interrupt as Record<string, unknown>).value
    : interrupt

  // The value may be the interrupt payload directly or wrapped in an array
  const candidates = Array.isArray(raw) ? raw : [raw]
  for (const item of candidates) {
    if (typeof item === 'object' && item !== null) {
      const obj = item as Record<string, unknown>
      if (obj.kind === 'widget' && obj.widget) {
        return obj as unknown as WidgetInterrupt
      }
    }
  }
  return null
}

export function RespondentChatPage(): React.ReactElement {
  const { shareToken, conversationId } = useParams<{ shareToken: string; conversationId: string }>()
  const [searchParams] = useSearchParams()
  const planId = searchParams.get('planId')
  const organizationId = searchParams.get('organizationId')
  const lang = searchParams.get('lang') ?? 'en'
  const dir = languageDirection(lang)
  const rtl = isRtl(lang)
  const [input, setInput] = useState('')
  const [jwt, setJwt] = useState<string | null>(null)
  const [started, setStarted] = useState(false)
  const threadRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setJwt(session?.access_token ?? null)
    })
  }, [])

  const thread = useStream({
    apiUrl: `${AGENT_URL}/interview`,
    assistantId: 'interview-agent',
    threadId: jwt ? (conversationId ?? null) : null,
    defaultHeaders: jwt ? { Authorization: `Bearer ${jwt}` } : {},
  })

  const historyLoaded = useRef(false)
  useEffect(() => {
    // Wait until thread history has genuinely loaded (isThreadLoading goes
    // true then back to false). Without this, the effect can fire before the
    // fetch starts and see an empty messages array on a populated thread.
    if (thread.isThreadLoading) {
      historyLoaded.current = true
    }
  }, [thread.isThreadLoading])

  useEffect(() => {
    if (started || !jwt || !planId || !organizationId) return
    if (thread.isLoading || thread.isThreadLoading || !historyLoaded.current) return
    if (thread.messages.length > 0 || thread.interrupt != null) {
      setStarted(true)
      return
    }
    setStarted(true)
    thread.submit(
      { messages: [{ type: 'human', content: 'Hi, I am ready to start the interview.' }] },
      { config: { configurable: { planId, organizationId, shareToken } } },
    )
  }, [started, jwt, planId, organizationId, shareToken, thread.isLoading, thread.isThreadLoading, thread.messages.length, thread.interrupt])

  useEffect(() => {
    const el = threadRef.current
    if (!el) return
    el.scrollTop = el.scrollHeight
  }, [thread.messages, thread.interrupt])

  useEffect(() => {
    const el = threadRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    // Keeps the transcript pinned to the bottom when its available height
    // shrinks — e.g. when a widget interrupt mounts below and would otherwise
    // hide the last message.
    const observer = new ResizeObserver(() => {
      el.scrollTop = el.scrollHeight
    })
    observer.observe(el)
    return (): void => observer.disconnect()
  }, [])

  const widgetInterrupt = getWidgetInterrupt(thread.interrupt)

  const handleSend = useCallback((): void => {
    const trimmed = input.trim()
    if (!trimmed || thread.isLoading || widgetInterrupt) return

    thread.submit(
      { messages: [{ type: 'human', content: trimmed }] },
      { config: { configurable: { planId, organizationId, shareToken } } },
    )
    setInput('')
  }, [input, thread, planId, organizationId])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>): void => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        handleSend()
      }
    },
    [handleSend],
  )

  const handleWidgetSubmit = useCallback(
    (response: WidgetResponse): void => {
      thread.submit(undefined, {
        command: { resume: response },
        config: { configurable: { planId, organizationId, shareToken } },
      })
    },
    [thread, planId, organizationId, shareToken],
  )

  const completed = isCompleted(thread.messages)

  if (completed) {
    return <ThankYouScreen shareToken={shareToken} lang={lang} />
  }

  const chatItems = buildChatItems(thread.messages, COMPLETION_MARKER)

  return (
    <main
      dir={dir}
      lang={lang}
      data-testid="chat-root"
      className="flex min-h-dvh flex-col bg-neutral-50"
    >
      <div className="mx-auto flex h-dvh w-full max-w-2xl flex-col">
        <div ref={threadRef} className="flex-1 overflow-y-auto p-4" data-testid="message-thread">
          <div className="flex flex-col gap-3">
            {chatItems.map((item, i) => {
              if (item.kind === 'widget-response') {
                const label = formatWidgetResponse(item.response)
                if (!label) return null
                return (
                  <ChatBubble key={item.id || i} variant="user" rtl={rtl}>
                    {label}
                  </ChatBubble>
                )
              }
              const isAi = item.role === 'ai'
              return (
                <ChatBubble key={item.id || i} variant={isAi ? 'ai' : 'user'} rtl={rtl}>
                  {isAi ? <MarkdownContent content={item.text} /> : item.text}
                </ChatBubble>
              )
            })}
            {thread.isLoading && <ChatTypingIndicator rtl={rtl} />}
          </div>
          {thread.error != null && (
            <div className="mt-3 rounded-md bg-error-50 p-3 text-sm text-error-700" data-testid="error-message">
              {thread.error instanceof Error ? thread.error.message : String(thread.error)}
            </div>
          )}
        </div>

        {widgetInterrupt && (
          <div className="border-t border-neutral-200 p-4">
            <ChatWidget
              config={widgetInterrupt.widget}
              onSubmit={handleWidgetSubmit}
            />
          </div>
        )}

        {!widgetInterrupt && (
          <div className="border-t border-neutral-200 p-3">
            <div className="flex gap-2">
              <textarea
                value={input}
                onChange={(e): void => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type your response..."
                rows={1}
                className="flex-1 resize-none rounded-md border border-neutral-300 bg-neutral-0 px-3 py-2 text-sm font-sans placeholder:text-neutral-500 focus:border-brand-500 focus:outline-hidden focus:ring-2 focus:ring-brand-500"
                data-testid="chat-input"
              />
              <Button
                variant="primary"
                size="md"
                onClick={handleSend}
                disabled={!input.trim() || thread.isLoading}
                data-testid="send-button"
              >
                Send
              </Button>
            </div>
          </div>
        )}
      </div>
    </main>
  )
}

function ThankYouScreen({
  shareToken,
  lang,
}: {
  readonly shareToken?: string | undefined
  readonly lang?: string | undefined
}): React.ReactElement {
  const dir = languageDirection(lang)
  const [email, setEmail] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleEmailSubmit = useCallback(async (): Promise<void> => {
    if (!email.trim()) return

    try {
      const { error: authError } = await supabase.auth.updateUser({ email })
      if (authError) throw authError

      const { data: { session } } = await supabase.auth.getSession()
      if (session?.access_token) {
        const agentUrl = (import.meta.env['VITE_AGENT_URL'] as string | undefined) ?? 'http://127.0.0.1:3001'
        const res = await fetch(`${agentUrl}/respondent/update-profile`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ email, shareToken }),
        })
        if (!res.ok) throw new Error('Failed to save profile')
      }
      setSubmitted(true)
    } catch {
      setError('Failed to save email.')
    }
  }, [email, shareToken])

  return (
    <main
      dir={dir}
      lang={lang}
      className="flex min-h-screen items-center justify-center bg-neutral-50 p-4"
      data-testid="thank-you"
    >
      <div className="w-full max-w-md text-center">
        <h1 className="text-2xl font-bold font-serif text-neutral-900 mb-2">Thank you!</h1>
        <p className="text-sm text-neutral-600 mb-6">
          Your responses have been recorded. We appreciate your time.
        </p>

        {!submitted && (
          <div className="text-left" data-testid="email-form">
            <p className="text-xs text-neutral-500 mb-2">
              Optionally, leave your email if you'd like to be contacted about this research.
            </p>
            <div className="flex gap-2">
              <input
                type="email"
                value={email}
                onChange={(e): void => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="flex-1 rounded-md border border-neutral-300 bg-neutral-0 px-3 py-2 text-sm font-sans placeholder:text-neutral-500 focus:border-brand-500 focus:outline-hidden focus:ring-2 focus:ring-brand-500"
                data-testid="email-input"
              />
              <Button
                variant="primary"
                size="md"
                onClick={handleEmailSubmit}
                disabled={!email.trim()}
                data-testid="email-submit"
              >
                Save
              </Button>
            </div>
            {error && (
              <p className="mt-2 text-xs text-error-700">{error}</p>
            )}
          </div>
        )}

        {submitted && (
          <p className="text-sm text-success-base">Email saved. Thank you!</p>
        )}
      </div>
    </main>
  )
}
